import React, { useEffect, useState } from "react";
import { Pencil } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

export const EditMatchDialog = ({ match, onClose, onSave }) => {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (match) {
      setForm({
        date: match.date || "",
        time: match.time || "",
        venue: match.venue || "",
        court_type: match.court_type || "",
        level: match.level || "",
        price: match.price || "",
        booking_link: match.booking_link || "",
        notes: match.notes || "",
        total_slots: match.total_slots || 4,
      });
    }
  }, [match]);

  if (!match || !form) return null;

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const valid = form.date && form.time && form.venue.trim();

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      await onSave(match.id, form);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!match} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#121b2d] border-[#1e2c4a] text-slate-100 max-w-md max-h-[90vh] overflow-y-auto" data-testid="edit-match-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <Pencil className="w-5 h-5 text-[#ccff00]" /> Modifica partita
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Aggiorna i dettagli della partita. Gli iscritti restano invariati.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Data">
              <Input type="date" value={form.date} onChange={(e) => set("date")(e.target.value)}
                data-testid="edit-match-date-input" className="bg-slate-900/60 border-slate-700 text-slate-100" />
            </Field>
            <Field label="Ora">
              <Input type="time" value={form.time} onChange={(e) => set("time")(e.target.value)}
                data-testid="edit-match-time-input" className="bg-slate-900/60 border-slate-700 text-slate-100" />
            </Field>
          </div>

          <Field label="Circolo / Campo">
            <Input placeholder="Es. Padel Club Milano" value={form.venue}
              onChange={(e) => set("venue")(e.target.value)}
              data-testid="edit-match-venue-input" className="bg-slate-900/60 border-slate-700 text-slate-100" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Tipo campo">
              <Select value={form.court_type} onValueChange={set("court_type")}>
                <SelectTrigger data-testid="edit-match-court-type-select" className="bg-slate-900/60 border-slate-700 text-slate-100">
                  <SelectValue placeholder="Scegli" />
                </SelectTrigger>
                <SelectContent className="bg-[#121b2d] border-[#1e2c4a] text-slate-100">
                  <SelectItem value="Coperto">Coperto</SelectItem>
                  <SelectItem value="All'aperto">All'aperto</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Livello">
              <Select value={form.level} onValueChange={set("level")}>
                <SelectTrigger data-testid="edit-match-level-select" className="bg-slate-900/60 border-slate-700 text-slate-100">
                  <SelectValue placeholder="Scegli" />
                </SelectTrigger>
                <SelectContent className="bg-[#121b2d] border-[#1e2c4a] text-slate-100">
                  <SelectItem value="Tutti">Tutti</SelectItem>
                  <SelectItem value="Principiante">Principiante</SelectItem>
                  <SelectItem value="Intermedio">Intermedio</SelectItem>
                  <SelectItem value="Avanzato">Avanzato</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Prezzo a testa">
              <Input placeholder="Es. 10€" value={form.price}
                onChange={(e) => set("price")(e.target.value)}
                data-testid="edit-match-price-input" className="bg-slate-900/60 border-slate-700 text-slate-100" />
            </Field>
            <Field label="Posti totali">
              <Select value={String(form.total_slots)} onValueChange={(v) => set("total_slots")(Number(v))}>
                <SelectTrigger data-testid="edit-match-slots-select" className="bg-slate-900/60 border-slate-700 text-slate-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121b2d] border-[#1e2c4a] text-slate-100">
                  <SelectItem value="2">2</SelectItem>
                  <SelectItem value="4">4</SelectItem>
                  <SelectItem value="6">6</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Note (opzionale)">
            <Textarea placeholder="Es. Portate l'acqua, campo prenotato alle 19" value={form.notes}
              onChange={(e) => set("notes")(e.target.value)}
              data-testid="edit-match-notes-input" className="bg-slate-900/60 border-slate-700 text-slate-100 resize-none" rows={2} />
          </Field>

          <Field label="Prenotazione campo (link o nota)">
            <Input placeholder="Es. https://... oppure 'Campo 3 prenotato'" value={form.booking_link}
              onChange={(e) => set("booking_link")(e.target.value)}
              data-testid="edit-match-booking-input" className="bg-slate-900/60 border-slate-700 text-slate-100" />
          </Field>
        </div>

        <DialogFooter>
          <Button
            disabled={!valid || busy}
            onClick={submit}
            data-testid="save-edit-match-button"
            className="w-full bg-[#ccff00] hover:bg-[#aeea00] text-[#090d16] font-bold rounded-xl h-11 disabled:opacity-40"
          >
            {busy ? "Salvataggio..." : "Salva modifiche"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</Label>
    {children}
  </div>
);
